import React from 'react';
import { Link } from 'react-router-dom';
import { FaChevronRight, FaPenNib, FaWifi, FaCheckCircle, FaStar } from 'react-icons/fa';
import useScrollAnimation from './useScrollAnimation';

const comparisonModels = [
  {
    id: 'i18-pro',
    name: 'Peace i18 PRO (5G Flagship)',
    tagline: 'Beyond Power. Beyond Limits.',
    priceBadge: '5G Flagship',
    image: '/Photo/i17-pro-hero.png',
    isHero: true,
    specs: {
      display: '6.61" FHD+ | 120Hz Immersive Vision',
      camera: '20MP Rear (Ultra Clarity AI) | 8MP Front',
      battery: '5000 mAh (All-Day Power, Fast Charging)',
      storage: '128GB Storage (More Space, More Freedom)',
      connectivity: '5G Ultra-Fast Connectivity | DUAL SIM',
      specialFeature: 'AI Intelligent Experience & Advanced Security',
      colors: 'Solar Orange, Titanium Silver, Obsidian Black',
      warranty: '1 Year Warranty (Peace Certified)'
    }
  },
  {
    id: 'nova-17-pro',
    name: 'Peace NOVA 18 PRO',
    tagline: 'The Ultimate Smartphone Experience',
    subtag: 'Powered for Everyday Excellence',
    priceBadge: '50MP Master Camera',
    image: '/Photo/nova-17-pro-hero.jpg',
    isHero: false,
    specs: {
      display: '6.56" HD+ Display',
      camera: '50MP Rear Camera | 8MP Front Camera',
      battery: '5000 mAh Inbuilt Battery',
      storage: '4GB RAM + 64GB ROM (Expandable up to 128GB)',
      connectivity: '4G Connectivity',
      specialFeature: 'Ultra Clarity Photography & Triple Color Selection',
      colors: 'Silver, Orange, Dark Gray',
      warranty: '1 Year Warranty'
    }
  },
  {
    id: 'sc26',
    name: 'Peace SC26 (Aura26)',
    tagline: 'Performance Meets Peace.',
    subtag: 'Smart. Stylish. Made for You.',
    priceBadge: 'Built-in Stylus Pen',
    image: '/Photo/sc26-hero.jpg',
    isHero: false,
    specs: {
      display: '6.56" HD+ Display',
      camera: '20MP Rear Camera | 8MP Front Camera',
      battery: '5000 mAh Long-lasting Battery',
      storage: '4GB+128GB or 6GB RAM+64GB ROM Options',
      connectivity: 'Dual SIM',
      specialFeature: 'Built-in Precision Stylus Pen Included',
      colors: 'Sunset Orange, Glacier Silver, Stealth Black',
      warranty: '1 Year Warranty (Peace of Mind, Every Day)'
    }
  }
];

const ComparisonSection = () => {
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <section 
      ref={sectionRef} 
      className={`comparison-section ${isVisible ? 'is-visible' : ''}`}
      id="compare"
    >
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center comparison-header scroll-fade-up">
          <span className="section-eyebrow">Lineup Comparison</span>
          <h2 className="section-headline">Side-by-Side Model Specs.</h2>
          <p className="section-subheadline">
            Compare key display, camera, battery, and connectivity specifications across all three models.
          </p>
        </div>

        {/* 3-Column Comparison Grid */}
        <div className="comparison-cards-grid three-column-grid">
          {comparisonModels.map((model) => (
            <div 
              key={model.id} 
              className={`compare-card scroll-fade-up ${model.isHero ? 'compare-card-highlight' : ''}`}
            >
              {model.isHero && (
                <div className="highlight-ribbon">
                  <FaStar className="me-1" /> 5G Flagship
                </div>
              )}

              <div className="compare-img-box">
                <img 
                  src={model.image} 
                  alt={model.name} 
                  className="compare-product-img"
                />
              </div>

              <div className="compare-model-info">
                <div className="compare-badge-tag">{model.priceBadge}</div>
                <h3 className="compare-name">{model.name}</h3>
                <p className="compare-tagline">{model.tagline}</p>
                {model.subtag && <p className="compare-subtag">{model.subtag}</p>}

                <div className="compare-actions">
                  <Link to={`/products/${model.id}`} className="compare-details-link">
                    Explore catalog <FaChevronRight className="mini-arrow" />
                  </Link>
                </div>
              </div>

              <hr className="compare-divider" />

              {/* Specs Breakdown */}
              <div className="compare-specs-list">
                <div className="compare-spec-item">
                  <div className="spec-label">Display</div>
                  <div className="spec-value font-highlight">{model.specs.display}</div>
                </div>

                <div className="compare-spec-item">
                  <div className="spec-label">Cameras</div>
                  <div className="spec-value">{model.specs.camera}</div>
                </div>

                <div className="compare-spec-item">
                  <div className="spec-label">Battery</div>
                  <div className="spec-value">{model.specs.battery}</div>
                </div>

                <div className="compare-spec-item">
                  <div className="spec-label">Memory & Storage</div>
                  <div className="spec-value">{model.specs.storage}</div>
                </div>

                <div className="compare-spec-item">
                  <div className="spec-label">Connectivity</div>
                  <div className="spec-value">{model.specs.connectivity}</div>
                </div>

                <div className="compare-spec-item">
                  <div className="spec-label">Special Feature</div>
                  <div className="spec-value feature-note">
                    {model.id === 'sc26' && <FaPenNib className="me-1 text-warning" />}
                    {model.id === 'i18-pro' && <FaWifi className="me-1 text-primary" />}
                    {model.specs.specialFeature}
                  </div>
                </div>

                <div className="compare-spec-item">
                  <div className="spec-label">Color Finishes</div>
                  <div className="spec-value">{model.specs.colors}</div>
                </div>

                <div className="compare-spec-item">
                  <div className="spec-label">Warranty</div>
                  <div className="spec-value text-success font-weight-bold">
                    <FaCheckCircle className="me-1" /> {model.specs.warranty}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
